import {
  SimpleUnchangedProps,
  addEffect,
  addHandlers,
  addProps,
  addState,
  flowMax,
} from 'ad-hok'
import { getContextHelpers, toObjectKeys } from 'ad-hok-utils'
import { addApi } from 'shared/src/api'
import { Bookmark, BookmarkInput } from 'shared/src/api/types'
import { addNavigationState } from 'shared/src/utils/addNavigationState'
import { addAuthContext } from 'shared/src/utils/auth'
import { typedAs } from 'shared/src/utils/typedAs'

const REFRESH_INTERVAL = 5 * 60 * 1000

interface BookmarkContext {
  getBookmark: (productId: number) => Bookmark | undefined
  addBookmark: (input: BookmarkInput) => void
  removeBookmark: (productId: number) => void
  numBookmarks: number
  bookmarkAfterLoggingIn: (input: BookmarkInput) => void
  triggerBookmarkAfterLoggingIn: () => void
}

export const [addBookmarkContextProviderInternal, addBookmarkContext] =
  getContextHelpers<BookmarkContext>(
    toObjectKeys([
      'getBookmark',
      'addBookmark',
      'removeBookmark',
      'numBookmarks',
      'bookmarkAfterLoggingIn',
      'triggerBookmarkAfterLoggingIn',
    ])
  )

export const addBookmarkContextProvider: SimpleUnchangedProps = flowMax(
  addApi,
  addState('bookmarks', 'setBookmarks', typedAs<Bookmark[]>([])),
  addNavigationState(
    'inputToSaveAfterLoggingIn',
    'bookmarkAfterLoggingIn',
    typedAs<BookmarkInput | null>(null)
  ),
  addProps(({ bookmarks }) => ({
    numBookmarks: bookmarks.length,
  })),
  addHandlers({
    addBookmark:
      ({ bookmarks, setBookmarks, api }) =>
      (input: BookmarkInput) => {
        api.createBookmark(input)

        setBookmarks([
          ...bookmarks.filter(
            (bookmark) => bookmark.productId !== input.productId
          ),
          { ...input, id: 0 },
        ])
      },
    removeBookmark:
      ({ bookmarks, setBookmarks, api }) =>
      (productId: number) => {
        const bookmark = bookmarks.find(
          (bookmark) => bookmark.productId === productId
        )

        if (bookmark) {
          api.deleteBookmark(bookmark.productId)
        }

        setBookmarks(
          bookmarks.filter((bookmark) => bookmark.productId !== productId)
        )
      },
    getBookmark:
      ({ bookmarks }) =>
      (productId: number) =>
        bookmarks.find((bookmark) => bookmark.productId === productId),
    refetchBookmarks:
      ({ setBookmarks, api }) =>
      () => {
        api
          .getBookmarks()
          .catch(() => [])
          .then(setBookmarks)
      },
  }),
  addAuthContext,
  addHandlers({
    triggerBookmarkAfterLoggingIn:
      ({ inputToSaveAfterLoggingIn, refetchBookmarks, api }) =>
      () => {
        if (inputToSaveAfterLoggingIn) {
          api.createBookmark(inputToSaveAfterLoggingIn).then(refetchBookmarks)
        }
      },
  }),
  addEffect(
    ({ isAuthenticated, setBookmarks, refetchBookmarks }) =>
      () => {
        setBookmarks([])

        if (isAuthenticated) {
          refetchBookmarks()

          const timer = setInterval(() => {
            refetchBookmarks()
          }, REFRESH_INTERVAL)

          return () => {
            clearInterval(timer)
          }
        }

        return
      },
    ['isAuthenticated']
  ),
  addBookmarkContextProviderInternal
)
